import { useEffect, useState, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Target, Save, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";

/**
 * Modal allowing the user to set coverage objectives per Mandant × Country.
 * Priority: per-cell override > principal default > global default.
 */
export default function CoverageObjectivesDialog({
  open,
  onOpenChange,
  principals = [],
  countries = [],
  onSaved,
}) {
  const [defaultTarget, setDefaultTarget] = useState(10);
  const [principalDefaults, setPrincipalDefaults] = useState({});
  const [overrides, setOverrides] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadObjectives = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coverage/objectives");
      setDefaultTarget(data.default_target ?? 10);
      setPrincipalDefaults(data.principal_defaults || {});
      setOverrides(data.overrides || {});
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadObjectives();
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/coverage/objectives", {
        default_target: Number(defaultTarget) || 10,
        principal_defaults: Object.fromEntries(
          Object.entries(principalDefaults)
            .filter(([, v]) => Number(v) > 0)
            .map(([k, v]) => [k, Number(v)])
        ),
        overrides: Object.fromEntries(
          Object.entries(overrides)
            .filter(([, v]) => Number(v) > 0)
            .map(([k, v]) => [k, Number(v)])
        ),
      });
      toast.success("Objectifs enregistrés");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm("Réinitialiser tous les objectifs à 10 contacts par défaut ?")) return;
    setDefaultTarget(10);
    setPrincipalDefaults({});
    setOverrides({});
  };

  const filledOverridesByPrincipal = useMemo(() => {
    const out = {};
    Object.entries(overrides).forEach(([k]) => {
      const [pid] = k.split("__");
      out[pid] = (out[pid] || 0) + 1;
    });
    return out;
  }, [overrides]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="coverage-objectives-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-4 w-4 text-emerald-600" />
            Objectifs de couverture
          </DialogTitle>
          <DialogDescription>
            Définissez combien de contacts vous visez par Mandant × Pays. Le Heat Map et le
            Morning Routine prioriseront les zones les plus en retard sur vos objectifs.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Chargement…</div>
        ) : (
          <div className="space-y-5">
            {/* Global default */}
            <div className="flex items-center gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/20">
              <div className="flex-1">
                <p className="text-sm font-medium">Objectif par défaut</p>
                <p className="text-[11px] text-muted-foreground">
                  Appliqué à toute cellule sans valeur spécifique
                </p>
              </div>
              <Input
                type="number"
                min={1}
                max={1000}
                value={defaultTarget}
                onChange={(e) => setDefaultTarget(e.target.value)}
                className="w-24 h-9"
                data-testid="default-target-input"
              />
              <span className="text-xs text-muted-foreground">contacts</span>
            </div>

            {/* Per-principal config */}
            <div className="space-y-2">
              <p className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">
                Par Mandant
              </p>
              {principals.length === 0 && (
                <p className="text-sm text-muted-foreground py-3 text-center">
                  Aucun Mandant disponible.
                </p>
              )}
              {principals.map((p) => {
                const isOpen = !!expanded[p.id];
                const pDefault = principalDefaults[p.id] ?? "";
                const nOverrides = filledOverridesByPrincipal[p.id] || 0;
                return (
                  <div key={p.id} className="rounded-lg border border-border/60 bg-card">
                    <div className="flex items-center gap-3 p-3">
                      <button
                        onClick={() => setExpanded({ ...expanded, [p.id]: !isOpen })}
                        className="p-1 hover:bg-muted/60 rounded"
                        data-testid={`expand-${p.id}`}
                      >
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{p.name}</p>
                        {nOverrides > 0 && (
                          <Badge variant="outline" className="text-[10px] mt-0.5">
                            {nOverrides} pays personnalisé{nOverrides > 1 ? "s" : ""}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">Par défaut :</span>
                        <Input
                          type="number"
                          min={0}
                          max={1000}
                          value={pDefault}
                          placeholder={String(defaultTarget)}
                          onChange={(e) =>
                            setPrincipalDefaults({
                              ...principalDefaults,
                              [p.id]: e.target.value,
                            })
                          }
                          className="w-20 h-8 text-sm"
                          data-testid={`principal-default-${p.id}`}
                        />
                      </div>
                    </div>
                    {isOpen && (
                      <div className="border-t border-border/40 p-3 bg-muted/20">
                        <p className="text-[11px] text-muted-foreground mb-2">
                          Surcharge par pays (laisser vide = utiliser la valeur par défaut du Mandant)
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {countries.map((c) => {
                            const key = `${p.id}__${c.code}`;
                            return (
                              <div key={c.code} className="flex items-center gap-2 text-xs">
                                <span className="w-8">{c.flag}</span>
                                <span className="flex-1 truncate" title={c.name}>
                                  {c.code}
                                </span>
                                <Input
                                  type="number"
                                  min={0}
                                  max={1000}
                                  value={overrides[key] ?? ""}
                                  placeholder={String(
                                    Number(pDefault) || defaultTarget
                                  )}
                                  onChange={(e) =>
                                    setOverrides({
                                      ...overrides,
                                      [key]: e.target.value,
                                    })
                                  }
                                  className="w-16 h-7 text-xs"
                                  data-testid={`override-${p.id}-${c.code}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-between gap-2 sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={saving}
            data-testid="reset-objectives-btn"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-2" /> Réinitialiser
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={handleSave} size="sm" disabled={saving} data-testid="save-objectives-btn">
              <Save className="h-3.5 w-3.5 mr-2" />
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
